/* src/pages/admin/AdminZipcodeDispatchZoneDetailPage.tsx */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import { adminZipcodeDispatchZoneService, AdminZipcodeDispatchZone } from "../../services/admin.zipcode-dispatch-zone.service";
import { adminDispatcherZipcodeService } from "../../services/admin.dispatcher-zipcode.service";
import { AdminDispatcher } from "../../services/admin.dispatcher.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faMapMarkedAlt,
    faCircleNotch,
    faTriangleExclamation,
    faGlobe,
    faMapPin,
    faUser,
    faTrash,
    faPlus,
    faAddressCard,
    faPhone,
    faEnvelope
} from "@fortawesome/free-solid-svg-icons";
import AssignDispatcherModal from "../../components/admin/AssignDispatcherModal";
import styles from "../../styles/pages/AdminDispatcherDetailPage.module.css";
import dispatchersStyles from "../../styles/pages/AdminDispatchersPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";

const AdminZipcodeDispatchZoneDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    const [zone, setZone] = useState<AdminZipcodeDispatchZone | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Dispatchers associated states
    const [dispatchers, setDispatchers] = useState<AdminDispatcher[]>([]);
    const [dispatchersLoading, setDispatchersLoading] = useState(false);
    const [dispatchersError, setDispatchersError] = useState<string | null>(null);

    // Modal state
    const [showAssignModal, setShowAssignModal] = useState(false);

    // Permissions check
    const hasZoneRead = permissionCheckerService.checkPermission(adminData, "ZIPCODE DISPATCH ZONE", "READ");
    const canAssign = permissionCheckerService.checkPermission(adminData, "ZIPCODE DISPATCH ZONE", "ASSIGN");

    const fetchDetails = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const response = await adminZipcodeDispatchZoneService.getZipcodeDispatchZoneById(Number(id));
            if (response.statusCode === 200) {
                setZone(response.data);
            } else {
                setError(response.message || "No se pudo cargar la zona de despacho.");
            }
        } catch (err: any) {
            console.error("Error fetching zipcode dispatch zone details:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const fetchDispatchers = async () => {
        if (!id || !hasZoneRead) return;
        try {
            setDispatchersLoading(true);
            setDispatchersError(null);
            const response = await adminDispatcherZipcodeService.getDispatchersByZone(Number(id));
            if (response.statusCode === 200) {
                setDispatchers(response.data);
            } else {
                setDispatchersError(response.message || "No se pudieron cargar los transportistas.");
            }
        } catch (err: any) {
            console.error("Error fetching dispatchers for zone:", err);
            setDispatchersError(errorService.getDisplayMessage(err));
        } finally {
            setDispatchersLoading(false);
        }
    };

    useEffect(() => {
        if (!hasZoneRead) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate, hasZoneRead]);

    useEffect(() => {
        fetchDetails();
        fetchDispatchers();
    }, [id]);

    const handleUnassignDispatcher = async (dispatcher: AdminDispatcher) => {
        const result = await Swal.fire({
            title: "¿Desasignar transportista?",
            text: `Esta acción removerá la asignación del transportista ${dispatcher.firstName} ${dispatcher.lastName} de esta zona.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: "Sí, desasignar",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setDispatchersLoading(true);
                const response = await adminDispatcherZipcodeService.unassignDispatcherFromZone({
                    idDispatcher: dispatcher.id,
                    idZipcodeDispatchZone: Number(id),
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "Desasignado",
                        text: "El transportista ha sido desasignado con éxito.",
                    });
                    fetchDispatchers();
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error unassigning dispatcher:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setDispatchersLoading(false);
            }
        }
    };

    const handleBack = () => {
        navigate("/admin/zipcode-dispatch-zones");
    };

    const columns: TableColumn<AdminDispatcher>[] = [
        {
            name: "Código",
            selector: (row) => row.code || "-",
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faAddressCard} className="text-muted" />
                    <span className="fw-semibold">{row.code || "-"}</span>
                </div>
            ),
            width: "140px",
        },
        {
            name: "Transportista",
            selector: (row) => `${row.firstName} ${row.lastName}`,
            sortable: true,
            cell: (row) => (
                <div className="d-flex flex-column py-2">
                    <span className={dispatchersStyles.dispatcherName}>{row.firstName} {row.lastName}</span>
                </div>
            ),
            minWidth: "220px",
        },
        {
            name: "Contacto",
            selector: (row) => row.email || "",
            cell: (row) => (
                <div className="d-flex flex-column py-2" style={{ fontSize: "12.5px" }}>
                    {row.phoneNumber && (
                        <span>
                            <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                            {row.phoneNumber}
                        </span>
                    )}
                    {row.email && (
                        <span className="text-muted">
                            <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                            {row.email}
                        </span>
                    )}
                </div>
            ),
            minWidth: "220px",
        },
        {
            name: "Estado",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span className={`${dispatchersStyles.badge} ${row.status === 1 ? dispatchersStyles.statusActive : dispatchersStyles.statusInactive}`}>
                    {row.status === 1 ? "Activo" : "Inactivo"}
                </span>
            ),
            width: "120px",
        },
        ...(canAssign
            ? [
                  {
                      name: "Acciones",
                      width: "120px",
                      cell: (row: AdminDispatcher) => (
                          <div className="d-flex gap-2">
                              <button
                                  className={dispatchersStyles.deleteButton}
                                  onClick={() => handleUnassignDispatcher(row)}
                                  title="Desasignar transportista"
                              >
                                  <FontAwesomeIcon icon={faTrash} />
                              </button>
                          </div>
                      ),
                      right: true,
                  },
              ]
            : [])
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "60px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.detailContainer}>
                <DashboardCard
                    icon={faMapMarkedAlt}
                    title={zone ? `Detalle Zona: ${zone.zipcode}` : "Detalle Zona de Despacho"}
                    description={zone ? `ID de Zona: #${zone.id}` : "Información de la zona de despacho."}
                />

                {loading ? (
                    <div className={styles.loadingContainer}>
                        <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                        <p>Cargando detalles de la zona de despacho...</p>
                    </div>
                ) : error ? (
                    <div className={styles.errorContainer}>
                        <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                        <h3>Error</h3>
                        <p>{error}</p>
                        <button className={btnStyles.primary} onClick={handleBack}>
                            Regresar
                        </button>
                    </div>
                ) : zone ? (
                    <>
                        <div className={styles.detailCard}>
                            <div className={styles.cardBody}>
                                <h3 className={styles.sectionTitle}>Información de la Zona</h3>
                                <div className={styles.infoGrid}>
                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>País</span>
                                        <span className={styles.infoValue}>
                                            <FontAwesomeIcon icon={faGlobe} className="me-2 text-muted" />
                                            {zone.iso2Country || zone.isO2Country}
                                        </span>
                                    </div>

                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>Código Postal</span>
                                        <span className={styles.infoValue}>
                                            <FontAwesomeIcon icon={faMapPin} className="me-2 text-muted" />
                                            {zone.zipcode}
                                        </span>
                                    </div>

                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>Ciudad</span>
                                        <span className={styles.infoValue}>
                                            {zone.city || "-"}
                                        </span>
                                    </div>

                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>Estado</span>
                                        <span className={styles.infoValue}>
                                            {zone.state || "-"}
                                        </span>
                                    </div>

                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>Estatus</span>
                                        <div className="mt-1">
                                            <span className={`${styles.badge} ${zone.status === 1 ? styles.statusActive : styles.statusInactive}`}>
                                                {zone.status === 1 ? "Activa" : "Inactiva"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={dispatchersStyles.tableWrapper} style={{ marginTop: "24px" }}>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h3 className={styles.sectionTitle} style={{ borderBottom: "none", marginBottom: 0, paddingBottom: 0 }}>
                                    <FontAwesomeIcon icon={faUser} className="me-2" style={{ color: "var(--brand-black)" }} />
                                    Transportistas Asignados
                                </h3>
                                {canAssign && dispatchers.length === 0 && (
                                    <button
                                        className={btnStyles.primary}
                                        onClick={() => setShowAssignModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-2" /> Asignar Transportista
                                    </button>
                                )}
                            </div>

                            {dispatchersError ? (
                                <div className={dispatchersStyles.emptyContainer}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                                    <h3>Error de Carga</h3>
                                    <p>{dispatchersError}</p>
                                    <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={fetchDispatchers}>
                                        Reintentar
                                    </button>
                                </div>
                            ) : dispatchers.length === 0 && !dispatchersLoading ? (
                                <div className={dispatchersStyles.emptyContainer}>
                                    <FontAwesomeIcon icon={faUser} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                                    <p>No hay transportistas asignados a esta zona de despacho.</p>
                                </div>
                            ) : (
                                <DataTable
                                    columns={columns}
                                    data={dispatchers}
                                    progressPending={dispatchersLoading}
                                    progressComponent={
                                        <div className={dispatchersStyles.loadingContainer}>
                                            <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={dispatchersStyles.loadingIcon} />
                                            <p>Cargando transportistas asignados...</p>
                                        </div>
                                    }
                                    customStyles={customStyles}
                                    responsive
                                    highlightOnHover
                                    noDataComponent={null}
                                />
                            )}
                        </div>
                    </>
                ) : null}
            </div>

            {zone && (
                <AssignDispatcherModal
                    show={showAssignModal}
                    onHide={() => setShowAssignModal(false)}
                    onSuccess={fetchDispatchers}
                    idZone={zone.id}
                />
            )}
        </AdminDashboardLayout>
    );
};

export default AdminZipcodeDispatchZoneDetailPage;
